import React, { useEffect, useState, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import { trackPurchase } from '@/utils/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_POLL_ATTEMPTS = 10;
const POLL_INTERVAL = 2000;

export default function PaymentSuccess() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking');
  const sessionId = searchParams.get('session_id');
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (sessionId) { pollPaymentStatus(); } else { setStatus('success'); }
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    if (attemptsRef.current >= MAX_POLL_ATTEMPTS) { setStatus('timeout'); return; }
    attemptsRef.current += 1;
    try {
      const response = await fetch(`${BACKEND_URL}/api/payments/stripe/status/${sessionId}`);
      const data = await response.json();
      if (data.payment_status === 'paid') {
        setStatus('success');
        // Fire Google Ads purchase conversion (dedup by transaction_id)
        if (data.order_id && data.total) {
          trackPurchase({ transactionId: data.order_id, value: data.total, currency: 'EUR' });
        }
        const cartSessionId = localStorage.getItem('sessionId');
        if (cartSessionId) { await fetch(`${BACKEND_URL}/api/cart/${cartSessionId}`, { method: 'DELETE' }); }
      } else if (data.status === 'expired') { setStatus('expired'); }
      else { setTimeout(pollPaymentStatus, POLL_INTERVAL); }
    } catch (error) { setTimeout(pollPaymentStatus, POLL_INTERVAL); }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-6">
      <div className="max-w-md w-full text-center">
        {status === 'checking' && (
          <>
            <Loader className="w-16 h-16 mx-auto text-gray-400 animate-spin mb-6" />
            <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Playfair Display' }}>{t('payment_success.checking')}</h2>
            <p className="text-gray-600">{t('payment_success.checking_desc')}</p>
          </>
        )}
        {status === 'success' && (
          <>
            <div className="bg-gray-100 rounded-full w-24 h-24 flex items-center justify-center mx-auto mb-6"><CheckCircle className="w-16 h-16" /></div>
            <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Playfair Display' }}>{t('payment_success.success')}</h2>
            <p className="text-gray-600 mb-8">{t('payment_success.success_desc')}</p>
            <div className="space-y-3">
              <Link to="/productos"><Button data-testid="continue-shopping-btn" className="w-full bg-black text-white hover:bg-gray-800 py-4">{t('payment_success.continue_shopping')}</Button></Link>
              <Link to="/"><Button data-testid="go-home-btn" variant="outline" className="w-full border-2 border-black py-4">{t('payment_success.go_home')}</Button></Link>
            </div>
          </>
        )}
        {status === 'timeout' && (
          <>
            <div className="bg-yellow-50 rounded-full w-24 h-24 flex items-center justify-center mx-auto mb-6"><AlertTriangle className="w-16 h-16 text-yellow-600" /></div>
            <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Playfair Display' }}>{t('payment_success.timeout')}</h2>
            <p className="text-gray-600 mb-8">{t('payment_success.timeout_desc')}</p>
            <div className="space-y-3">
              <Link to="/"><Button data-testid="go-home-timeout-btn" className="w-full bg-black text-white hover:bg-gray-800 py-4">{t('payment_success.go_home')}</Button></Link>
              <Link to="/contacto"><Button data-testid="contact-btn" variant="outline" className="w-full border-2 border-black py-4">{t('payment_success.contact')}</Button></Link>
            </div>
          </>
        )}
        {status === 'expired' && (
          <>
            <div className="bg-red-50 rounded-full w-24 h-24 flex items-center justify-center mx-auto mb-6"><AlertTriangle className="w-16 h-16 text-red-600" /></div>
            <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Playfair Display' }}>{t('payment_success.expired')}</h2>
            <p className="text-gray-600 mb-8">{t('payment_success.expired_desc')}</p>
            <div className="space-y-3">
              <Link to="/checkout"><Button data-testid="retry-checkout-btn" className="w-full bg-black text-white hover:bg-gray-800 py-4">{t('payment_success.retry')}</Button></Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
